import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import CollectionQuery from '../../interfaces/collection-query.interface';
import DocumentReadQuery from '../../interfaces/document-read-query.interface';
import DocumentWriteQuery from '../../interfaces/document-write-query.interface';
import { CustomError } from '../error/error.util';

/**
 * Initialize the firebase app
 */
export const init = (): void => {
  if (firebase.apps.length) {
    return;
  }

  firebase.initializeApp({
    apiKey: process.env.REACT_APP_FIREBASE_APIKEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTHDOMAIN,
    databaseURL: process.env.REACT_APP_FIREBASE_DATABASEURL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECTID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGEBUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGINGSENDERID,
    appId: process.env.REACT_APP_FIREBASE_APPID,
  });
};

// Auto init
init();

/** Notifies about every change in firebase.auth() */
export const authState$ = new Observable<firebase.User>((subscriber) => {
  const unsubscribe = firebase.auth().onAuthStateChanged(subscriber);

  return { unsubscribe };
});

/** Resolves when the firebase connection is initialized */
export const isInitialized$ = authState$.pipe(first()).toPromise();

/** Synchronously determine whether the firebase connection is initialized. */
export const isInitialized = (): boolean => !!firebase.apps.length;

/**
 * Tiny helper to convert the firebase timestamps to milliseconds.
 *
 * @example
 *  convertTimestamps(documentFromFirebase)
 */
export const convertTimestamps = (
  doc: firebase.firestore.DocumentData
): firebase.firestore.DocumentData => ({
  ...doc,
  ...(doc.createdAt
    ? { createdAt: ((doc.createdAt as unknown) as firebase.firestore.Timestamp).toMillis() }
    : {}),
  ...(doc.updatedAt
    ? { updatedAt: ((doc.updatedAt as unknown) as firebase.firestore.Timestamp).toMillis() }
    : {}),
});

/**
 * Save common data/documents into the database.
 *
 * @returns the document's id
 *
 * @example
 *  await saveDocument$<User>({
 *    collection: 'users',
 *    doc: 'R1rXq1PCOQYP9R00u4MHQ1o2eg31',
 *    data: { ... },
 *  });
 *
 *  await saveDocument$<Analysis>({
 *    collection: 'analyses',
 *    data: { ... },
 *  })
 */
export const saveDocument$ = async <T>({
  collection,
  doc,
  data,
  options,
  timestamps = true,
}: DocumentWriteQuery<T>): Promise<string> => {
  await isInitialized$;

  const db = firebase.firestore();

  if (!db) {
    throw new CustomError({ ...CustomError.errors.backend.DataNoDB });
  }

  try {
    const payload = {
      ...data,
      ...(timestamps
        ? {
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          }
        : {}),
    };

    if (doc) {
      await db.collection(collection).doc(doc).set(payload, options);

      return doc;
    }

    const result = await db.collection(collection).add(payload);

    return result.id;
  } catch (error) {
    throw new CustomError({ ...CustomError.errors.backend.DataWriteError, originalError: error });
  }
};

/**
 * Get a single document from the database by it's id.
 *
 * Attention:
 * Don't forget to unsubscribe (or tie the subscription to the authState$).
 *
 * @example
 *  const doc$ = queryDocument<User>({
 *    collection: 'users',
 *    doc: 'R1rXq1PCOQYP9R00u4MHQ1o2eg31',
 *  });
 *    => Returns an observable containing the document's data.
 *    => Throws an error, if the document does not exist.
 *
 *  const doc$ = queryDocument<User>({
 *    collection: 'users',
 *    doc: 'R1rXq1PCOQYP9R00u4MHQ1o2eg31',
 *    isRequired: false,
 *  });
 *    => Returns an observable containing the document's data (omitted if the document does not exist yet).
 *    => No error will be thrown, if the document does not exist (yet).
 */
export const queryDocument$ = <T>({
  collection,
  doc,
  isRequired = true,
}: DocumentReadQuery): Observable<T> => {
  const db = firebase.firestore();

  if (!db) {
    throw new CustomError({ ...CustomError.errors.backend.DataNoDB });
  }

  return new Observable<T>((subscriber) => {
    const unsubscribe = db
      .collection(collection)
      .doc(doc)
      .onSnapshot(
        (document) => {
          if (isRequired && (!document || !document.exists)) {
            subscriber.error(new CustomError({ ...CustomError.errors.backend.DataNotFound }));
            return;
          }

          subscriber.next(
            (convertTimestamps({ id: document.id, ...document.data() }) as unknown) as T
          );
        },
        (error) => {
          subscriber.error(
            new CustomError({
              ...CustomError.errors.backend.DataDocumentReadError,
              originalError: error,
            })
          );
        }
      );

    return { unsubscribe };
  });
};

/**
 * Query a collection of documents.
 *
 * Attention:
 * - Don't forget to unsubscribe (or tie the subscription to the authState$).
 * - The `where` param is non-optional since generally you want at least to limit the `owner`
 *
 * @example
 *  const analyses$ = queryCollection<Analysis>({
 *    collection: 'analyses',
 *    where: [['owner', '==', user.uid]],
 *  });
 */
export const queryCollection$ = <T>({
  collection,
  from,
  limit,
  order,
  where,
}: CollectionQuery): Observable<T[]> => {
  const db = firebase.firestore();

  if (!db) {
    throw new CustomError({ ...CustomError.errors.backend.DataNoDB });
  }

  return new Observable<T[]>((subscriber) => {
    const col = db.collection(collection);
    let query;

    // Filtering
    query = where.reduce<firebase.firestore.Query>((prev, curr) => prev.where(...curr), col);

    // Order
    if (order && order.length) {
      query = order.reduce((prev, curr) => prev.orderBy(...curr), query);
    }

    // Pagination
    if (from) {
      query = query.startAt(from);
    }

    // Limit
    if (limit) {
      query = query.limit(limit);
    }

    const unsubscribe = query.onSnapshot(
      (result) => {
        subscriber.next(
          (result.docs.map((doc) =>
            convertTimestamps({ id: doc.id, ...doc.data() })
          ) as unknown) as T[]
        );
      },
      (error) => {
        subscriber.error(
          new CustomError({
            ...CustomError.errors.backend.DataCollectionReadError,
            originalError: error,
          })
        );
      }
    );

    return { unsubscribe };
  });
};
